import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sltAPI } from '../../services/api';
import SLTLayout from './SLTLayout';
import { BRAND, cardStyle, tdStyle, searchInputStyle, formatDate, StatPill, SortableTh, sortRows, Paginator, ClickableRow, EmptyState } from './sltUi';

const PAGE_SIZE = 15;

const SLTTeachers = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'lastActivity', dir: 'desc' });
  const [page, setPage] = useState(1);

  useEffect(() => {
    let isMounted = true;
    sltAPI.getTeachers({ limit: 100 })
      .then((res) => { if (isMounted) setData(res.data); })
      .catch(() => { if (isMounted) setError('Unable to load teachers.'); });
    return () => { isMounted = false; };
  }, []);

  const handleSort = (key) => {
    setPage(1);
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
  };

  const filtered = useMemo(() => {
    const rows = data?.teachers || [];
    const term = search.trim().toLowerCase();
    const matched = term
      ? rows.filter((t) => `${t.name} ${t.email}`.toLowerCase().includes(term))
      : rows;
    return sortRows(matched, sort);
  }, [data, search, sort]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <SLTLayout
      user={user}
      onLogout={onLogout}
      title="Teachers"
      subtitle="Every teacher in your school, with the volume of content they have generated. Click a teacher to see their classes, lesson plans and homework."
      actions={(
        <input
          type="search"
          aria-label="Search teachers"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by name or email…"
          style={searchInputStyle}
        />
      )}
    >
      {error && <p style={{ color: '#B3261E', fontWeight: 700 }}>{error}</p>}
      {!data && !error && <p style={{ color: BRAND.muted }}>Loading teachers…</p>}

      {data && (
        <section style={{ ...cardStyle, overflowX: 'auto', padding: 0 }}>
          {filtered.length === 0
            ? <EmptyState title="No teachers match your search" hint="Try a different name or email." />
            : (
              <>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <SortableTh label="Teacher" sortKey="name" sort={sort} onSort={handleSort} />
                      <SortableTh label="Students" sortKey="studentCount" sort={sort} onSort={handleSort} />
                      <SortableTh label="Lesson plans" sortKey="lessonPlanCount" sort={sort} onSort={handleSort} />
                      <SortableTh label="Homeworks" sortKey="homeworkCount" sort={sort} onSort={handleSort} />
                      <SortableTh label="Tutorials" sortKey="tutorialsGenerated" sort={sort} onSort={handleSort} />
                      <SortableTh label="Completion" sortKey="tutorialCompletionRate" sort={sort} onSort={handleSort} />
                      <SortableTh label="Last activity" sortKey="lastActivity" sort={sort} onSort={handleSort} />
                      <th style={{ ...tdStyle, borderBottom: `2px solid ${BRAND.charcoal}` }} aria-hidden="true" />
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((t) => (
                      <ClickableRow key={t.uid} onActivate={() => navigate(`/slt/teachers/${t.uid}`)}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 800 }}>{t.name}</div>
                          <div style={{ color: BRAND.muted, fontSize: '0.8rem' }}>{t.email}</div>
                        </td>
                        <td style={tdStyle}>{t.studentCount}</td>
                        <td style={tdStyle}>{t.lessonPlanCount}</td>
                        <td style={tdStyle}>{t.homeworkCount}</td>
                        <td style={tdStyle}>{t.tutorialsGenerated}{t.tutorialsCompleted ? ` (${t.tutorialsCompleted} done)` : ''}</td>
                        <td style={tdStyle}><StatPill value={t.tutorialCompletionRate} /></td>
                        <td style={tdStyle}>{formatDate(t.lastActivity)}</td>
                        <td style={{ ...tdStyle, color: BRAND.muted, textAlign: 'right' }} aria-hidden="true">›</td>
                      </ClickableRow>
                    ))}
                  </tbody>
                </table>
                <Paginator
                  page={page}
                  totalPages={totalPages}
                  onPage={setPage}
                  summary={`${filtered.length} teacher${filtered.length === 1 ? '' : 's'}${search ? ' matching search' : ''}`}
                />
              </>
            )}
        </section>
      )}
    </SLTLayout>
  );
};

export default SLTTeachers;
